import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, Hammer, Plus, Pencil, Trash2, Library, ListChecks, Wrench, AlertCircle, Sparkles,
} from 'lucide-react';
import NewQuoteWizard from '../../components/admin/NewQuoteWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtZar = (n) =>
  `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const tradeLabel = (id, trades) =>
  trades.find((t) => t.id === id)?.label || id;

// ---------------------------------------------------------------------------
// AdminQuotes — Construction Quote Assistant
// Phase 1A: rate library CRUD + quotes list scaffolding (no AI yet).
// ---------------------------------------------------------------------------
const AdminQuotes = () => {
  const [tab, setTab] = useState('quotes'); // 'quotes' | 'materials' | 'labor'
  const [meta, setMeta] = useState({ trades: [], vat_rate: 0.15, currency: 'ZAR' });

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/admin/quotes/meta/trades`, { headers: authHeaders() })
      .then((r) => setMeta(r.data))
      .catch(() => {});
  }, []);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Calculator className="h-8 w-8" style={{ color: '#00CED1' }} />
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-quotes-heading">Quote AI</h1>
            <p className="text-sm text-gray-500">Build branded quotes for builders, painters, electricians, plumbers and more.</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">Phase 1D · Export & deliver</Badge>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6 flex gap-1 overflow-x-auto" role="tablist">
        <TabButton active={tab === 'quotes'} onClick={() => setTab('quotes')} icon={ListChecks} label="Quotes" testid="admin-quotes-tab-quotes" />
        <TabButton active={tab === 'materials'} onClick={() => setTab('materials')} icon={Library} label="Material rates" testid="admin-quotes-tab-materials" />
        <TabButton active={tab === 'labor'} onClick={() => setTab('labor')} icon={Wrench} label="Labor rates" testid="admin-quotes-tab-labor" />
      </div>

      {tab === 'quotes' && <QuotesList trades={meta.trades} />}
      {tab === 'materials' && <RateLibrary kind="material" trades={meta.trades} />}
      {tab === 'labor' && <RateLibrary kind="labor" trades={meta.trades} />}
    </AdminLayout>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
      active
        ? 'border-teal-500 text-teal-600'
        : 'border-transparent text-gray-500 hover:text-gray-800'
    }`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

// ---------------------------------------------------------------------------
// Quotes list — with "New quote" wizard (Phase 1B)
// ---------------------------------------------------------------------------
const QuotesList = ({ trades }) => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/quotes`, { headers: authHeaders() });
      setQuotes(r.data || []);
    } catch (e) {
      toast.error('Could not load quotes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  return (
    <div data-testid="admin-quotes-list">
      <Card className="mb-6 border-dashed" style={{ backgroundColor: 'rgba(0,206,209,0.04)' }}>
        <CardContent className="p-5 flex items-start gap-3">
          <Sparkles className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#00CED1' }} />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-1">Quote AI · Phase 1D is live!</p>
            <p>
              Click <strong>New quote</strong> to upload a plan — Gemini 3 Pro auto-builds the line items.
              Then open any saved quote to edit lines, drag-reorder, download branded PDF/XLSX, and send via email or WhatsApp.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Saved quotes</h2>
        <Button
          onClick={() => setWizardOpen(true)}
          className="text-white font-semibold"
          style={{ backgroundColor: '#00CED1' }}
          data-testid="admin-quotes-new"
        >
          <Plus className="h-4 w-4 mr-2" />
          New quote
        </Button>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {!loading && quotes.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-gray-500">
            No quotes yet. Click <strong>New quote</strong> to upload your first plan.
          </CardContent>
        </Card>
      )}
      {!loading && quotes.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="w-full text-sm" data-testid="admin-quotes-table">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Trade</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr
                  key={q.id}
                  className="border-t hover:bg-teal-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/quotes/${q.id}`)}
                  data-testid={`admin-quote-row-${q.id}`}
                >
                  <td className="px-4 py-3 font-medium">{q.title}</td>
                  <td className="px-4 py-3">{q.client_name || '—'}</td>
                  <td className="px-4 py-3 capitalize">{tradeLabel(q.trade, trades)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtZar(q.total)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{q.status || 'draft'}</Badge></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{q.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewQuoteWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        trades={trades}
        onCreated={(quote) => {
          fetchQuotes();
          // Jump straight into the editor so the user can refine
          if (quote?.id) navigate(`/admin/quotes/${quote.id}`);
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// RateLibrary — material / labor CRUD table with filter + add/edit/delete
// ---------------------------------------------------------------------------
const RateLibrary = ({ kind, trades }) => {
  const isMaterial = kind === 'material';
  const apiPath = isMaterial ? 'material-rates' : 'labor-rates';
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradeFilter, setTradeFilter] = useState('all');
  const [editing, setEditing] = useState(null); // {existing}|null
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/quotes/${apiPath}`, { headers: authHeaders() });
      setRates(r.data || []);
    } catch (e) {
      toast.error('Could not load rates');
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const filtered = useMemo(() => {
    if (tradeFilter === 'all') return rates;
    return rates.filter((r) => r.trade === tradeFilter);
  }, [rates, tradeFilter]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/quotes/${apiPath}/${confirmDelete.id}`, {
        headers: authHeaders(),
      });
      toast.success('Deleted');
      setConfirmDelete(null);
      await fetchRates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid={`admin-quotes-rate-library-${kind}`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label htmlFor={`${kind}-trade-filter`} className="text-xs text-gray-500">Filter by trade</Label>
          <Select value={tradeFilter} onValueChange={setTradeFilter}>
            <SelectTrigger className="w-48" id={`${kind}-trade-filter`} data-testid={`admin-quotes-${kind}-trade-filter`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All trades</SelectItem>
              {trades.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-500">{filtered.length} item(s)</span>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="text-white font-semibold"
          style={{ backgroundColor: '#00CED1' }}
          data-testid={`admin-quotes-${kind}-add`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add {isMaterial ? 'material' : 'labor'} rate
        </Button>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No {isMaterial ? 'material' : 'labor'} rates in this trade.
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="w-full text-sm" data-testid={`admin-quotes-${kind}-table`}>
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Trade</th>
                <th className="px-4 py-3">{isMaterial ? 'Material' : 'Role'}</th>
                {isMaterial && <th className="px-4 py-3">Unit</th>}
                {isMaterial && <th className="px-4 py-3">Category</th>}
                <th className="px-4 py-3 text-right">{isMaterial ? 'Unit price' : 'Hourly rate'}</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`admin-quotes-${kind}-row-${r.id}`}>
                  <td className="px-4 py-3 capitalize">{tradeLabel(r.trade, trades)}</td>
                  <td className="px-4 py-3 font-medium">{r.name || r.role}</td>
                  {isMaterial && <td className="px-4 py-3 text-gray-500">{r.unit}</td>}
                  {isMaterial && <td className="px-4 py-3 text-gray-500">{r.category || '—'}</td>}
                  <td className="px-4 py-3 text-right font-mono">
                    {fmtZar(isMaterial ? r.unit_price_zar : r.hourly_rate_zar)}
                    {!isMaterial && <span className="text-xs text-gray-500"> /h</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-teal-600"
                        title="Edit"
                        data-testid={`admin-quotes-${kind}-edit-${r.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(r)}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                        title="Delete"
                        data-testid={`admin-quotes-${kind}-delete-${r.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RateEditor
        kind={kind}
        trades={trades}
        open={creating || !!editing}
        rate={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); fetchRates(); }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title={`Delete this ${isMaterial ? 'material' : 'labor'} rate?`}
        description={confirmDelete ? `"${confirmDelete.name || confirmDelete.role}" will be permanently removed.` : ''}
        confirmLabel="Delete"
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        testId={`admin-quotes-${kind}-confirm-delete`}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// RateEditor — add/edit modal for material or labor rates
// ---------------------------------------------------------------------------
const RateEditor = ({ kind, trades, open, rate, onClose, onSaved }) => {
  const isMaterial = kind === 'material';
  const apiPath = isMaterial ? 'material-rates' : 'labor-rates';
  const blank = isMaterial
    ? { trade: trades[0]?.id || 'builder', name: '', unit: 'each', unit_price_zar: 0, category: '', notes: '' }
    : { trade: trades[0]?.id || 'builder', role: '', hourly_rate_zar: 0, notes: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rate) setForm({ ...blank, ...rate });
    else setForm(blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, open]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('admin_token')}` };
      if (rate?.id) {
        await axios.put(`${BACKEND_URL}/api/admin/quotes/${apiPath}/${rate.id}`, form, { headers });
        toast.success('Updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/quotes/${apiPath}`, form, { headers });
        toast.success('Created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`admin-quotes-${kind}-editor`}>
        <DialogHeader>
          <DialogTitle>{rate?.id ? 'Edit' : 'Add'} {isMaterial ? 'material' : 'labor'} rate</DialogTitle>
          <DialogDescription>
            Rates are stored in ZAR and used by the AI quote builder to price line items.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs">Trade</Label>
            <Select value={form.trade} onValueChange={(v) => update('trade', v)}>
              <SelectTrigger data-testid={`admin-quotes-${kind}-editor-trade`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {trades.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isMaterial ? (
            <>
              <div>
                <Label className="text-xs">Material name</Label>
                <Input
                  value={form.name || ''}
                  onChange={(e) => update('name', e.target.value)}
                  required
                  placeholder="e.g. Cement (50kg bag)"
                  data-testid="admin-quotes-material-editor-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Unit</Label>
                  <Input
                    value={form.unit || ''}
                    onChange={(e) => update('unit', e.target.value)}
                    placeholder="m² / m / bag / each"
                    data-testid="admin-quotes-material-editor-unit"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unit price (ZAR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.unit_price_zar ?? 0}
                    onChange={(e) => update('unit_price_zar', parseFloat(e.target.value) || 0)}
                    required
                    data-testid="admin-quotes-material-editor-price"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Category (optional)</Label>
                <Input
                  value={form.category || ''}
                  onChange={(e) => update('category', e.target.value)}
                  placeholder="structural / finishes / paint / ..."
                  data-testid="admin-quotes-material-editor-category"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">Role</Label>
                <Input
                  value={form.role || ''}
                  onChange={(e) => update('role', e.target.value)}
                  required
                  placeholder="e.g. Senior bricklayer"
                  data-testid="admin-quotes-labor-editor-role"
                />
              </div>
              <div>
                <Label className="text-xs">Hourly rate (ZAR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.hourly_rate_zar ?? 0}
                  onChange={(e) => update('hourly_rate_zar', parseFloat(e.target.value) || 0)}
                  required
                  data-testid="admin-quotes-labor-editor-rate"
                />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              value={form.notes || ''}
              onChange={(e) => update('notes', e.target.value)}
              data-testid={`admin-quotes-${kind}-editor-notes`}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid={`admin-quotes-${kind}-editor-cancel`}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="text-white font-semibold"
              style={{ backgroundColor: '#00CED1' }}
              data-testid={`admin-quotes-${kind}-editor-save`}
            >
              {saving ? 'Saving…' : (rate?.id ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminQuotes;
